type BuzzAdHostMode = 'production' | 'test' | 'disabled';

interface AdSenseQueue extends Array<unknown> {
  pauseAdRequests?: number;
  requestNonPersonalizedAds?: number;
}

interface TcfData {
  eventStatus?: string;
  listenerId?: number;
}

type TcfCallback = (data: TcfData, success: boolean) => void;
type TcfApi = (
  command: string,
  version: number,
  callback: TcfCallback,
  parameter?: number
) => void;

interface GoogleFundingChoices {
  callbackQueue: Array<() => void>;
  showRevocationMessage?: () => void;
}

declare global {
  interface Window {
    adsbygoogle?: AdSenseQueue;
    googlefc?: GoogleFundingChoices;
    __tcfapi?: TcfApi;
  }
}

export type WebAdPrivacyOptionsResult =
  | 'shown'
  | 'not-available'
  | 'disabled'
  | 'preview'
  | 'error';

const ADSENSE_ENABLED = import.meta.env.VITE_BUZZ_ADS_ENABLED === 'true';
const ADSENSE_CLIENT_ID = (
  import.meta.env.VITE_ADSENSE_CLIENT_ID || 'ca-pub-5166244917942863'
).trim();
const ADSENSE_SLOT_WEB_FEED = (
  import.meta.env.VITE_BUZZ_ADSENSE_SLOT_WEB_FEED ||
  import.meta.env.VITE_BUZZ_ADSENSE_SLOT_DESKTOP ||
  import.meta.env.VITE_BUZZ_ADSENSE_SLOT_MOBILE ||
  ''
).trim();
const ADSENSE_LAYOUT_KEY_WEB_FEED = (
  import.meta.env.VITE_BUZZ_ADSENSE_LAYOUT_KEY_WEB_FEED || '-fb+5w+4e-db+86'
).trim();
const ADSENSE_PREVIEW_TEST_ADS_ENABLED =
  import.meta.env.VITE_BUZZ_ADSENSE_PREVIEW_TEST_ADS === 'true';

let adSenseScriptPromise: Promise<void> | null = null;
let adSensePreparationPromise: Promise<boolean> | null = null;

export function getBuzzAdSenseConfig() {
  return {
    clientId: ADSENSE_CLIENT_ID,
    slotId: ADSENSE_SLOT_WEB_FEED,
    layoutKey: ADSENSE_LAYOUT_KEY_WEB_FEED,
  };
}

export function isBuzzWebAdsEnabled(): boolean {
  return getBuzzAdHostMode() !== 'disabled' && hasBuzzAdSenseSlot();
}

export function getBuzzAdHostMode(): BuzzAdHostMode {
  if (!ADSENSE_ENABLED) {
    return 'disabled';
  }

  const hostname = typeof window === 'undefined' ? '' : window.location.hostname.toLowerCase();
  if (hostname === 'playtark.com' || hostname === 'www.playtark.com') {
    return 'production';
  }

  if (
    ADSENSE_PREVIEW_TEST_ADS_ENABLED &&
    (hostname === 'preview.playtark.com' || hostname === 'localhost' || hostname === '127.0.0.1')
  ) {
    return 'test';
  }

  return 'disabled';
}

export async function prepareBuzzAdSense(): Promise<boolean> {
  const hostMode = getBuzzAdHostMode();
  if (hostMode === 'test') {
    return true;
  }
  if (hostMode !== 'production' || !hasBuzzAdSenseSlot()) {
    return false;
  }

  if (adSensePreparationPromise) {
    return adSensePreparationPromise;
  }

  adSensePreparationPromise = (async () => {
    const queue = getAdSenseQueue();
    queue.pauseAdRequests = 1;
    queue.requestNonPersonalizedAds = 1;

    try {
      await loadAdSenseScript();
      const consentReady = await waitForGoogleConsentReady();
      if (!consentReady) {
        adSensePreparationPromise = null;
        return false;
      }

      queue.requestNonPersonalizedAds = 1;
      queue.pauseAdRequests = 0;
      return true;
    } catch (error) {
      console.error('Failed to prepare BUZZ AdSense', error);
      adSensePreparationPromise = null;
      return false;
    }
  })();

  return adSensePreparationPromise;
}

export async function showGoogleAdPrivacyOptions(): Promise<WebAdPrivacyOptionsResult> {
  const hostMode = getBuzzAdHostMode();
  if (hostMode === 'disabled' || !hasBuzzAdSenseSlot()) {
    return 'disabled';
  }
  if (hostMode === 'test') {
    return 'preview';
  }

  const queue = getAdSenseQueue();
  queue.requestNonPersonalizedAds = 1;

  try {
    await loadAdSenseScript();
    const showRevocationMessage = await waitForRevocationFunction();
    if (!showRevocationMessage) {
      return 'not-available';
    }

    const googlefc = getGoogleFundingChoices();
    googlefc.callbackQueue.push(showRevocationMessage);
    return 'shown';
  } catch (error) {
    console.error('Failed to open Google ad privacy choices', error);
    return 'error';
  }
}

function hasBuzzAdSenseSlot(): boolean {
  return Boolean(ADSENSE_CLIENT_ID) && Boolean(ADSENSE_SLOT_WEB_FEED);
}

function getAdSenseQueue(): AdSenseQueue {
  if (!window.adsbygoogle) {
    window.adsbygoogle = [];
  }
  return window.adsbygoogle;
}

function getGoogleFundingChoices(): GoogleFundingChoices {
  if (!window.googlefc) {
    window.googlefc = { callbackQueue: [] };
  }
  if (!Array.isArray(window.googlefc.callbackQueue)) {
    window.googlefc.callbackQueue = [];
  }
  return window.googlefc;
}

function loadAdSenseScript(): Promise<void> {
  if (adSenseScriptPromise) {
    return adSenseScriptPromise;
  }

  adSenseScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src*="pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT_ID}"]`
    );

    if (existing) {
      if (existing.dataset.loaded === 'true') {
        resolve();
        return;
      }

      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener(
        'error',
        () => {
          adSenseScriptPromise = null;
          reject(new Error('AdSense script failed to load'));
        },
        { once: true }
      );
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT_ID}`;
    script.crossOrigin = 'anonymous';
    script.dataset.privacyTreatments = 'disablePersonalization';
    script.addEventListener(
      'load',
      () => {
        script.dataset.loaded = 'true';
        resolve();
      },
      { once: true }
    );
    script.addEventListener(
      'error',
      () => {
        adSenseScriptPromise = null;
        reject(new Error('AdSense script failed to load'));
      },
      { once: true }
    );
    document.head.appendChild(script);
  });

  return adSenseScriptPromise;
}

async function waitForGoogleConsentReady(): Promise<boolean> {
  await delay(250);
  const tcfApi = window.__tcfapi;
  if (typeof tcfApi !== 'function') {
    return true;
  }

  return new Promise((resolve) => {
    let settled = false;
    const timeoutId = window.setTimeout(() => finish(false), 60_000);

    const finish = (ready: boolean, listenerId?: number) => {
      if (settled) {
        return;
      }
      settled = true;
      window.clearTimeout(timeoutId);
      if (listenerId !== undefined) {
        tcfApi('removeEventListener', 2, () => undefined, listenerId);
      }
      resolve(ready);
    };

    tcfApi('addEventListener', 2, (tcData, success) => {
      if (!success) {
        return;
      }

      if (tcData.eventStatus === 'tcloaded' || tcData.eventStatus === 'useractioncomplete') {
        finish(true, tcData.listenerId);
      }
    });
  });
}

async function waitForRevocationFunction(): Promise<(() => void) | null> {
  for (let attempt = 0; attempt < 20; attempt += 1) {
    const showRevocationMessage = window.googlefc?.showRevocationMessage;
    if (typeof showRevocationMessage === 'function') {
      return showRevocationMessage;
    }
    await delay(100);
  }
  return null;
}

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}
